// components/superAdmin/SuperAdminSettingsTab.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, Switch, TextField, Button, Alert, ToggleButton, ToggleButtonGroup, Skeleton } from '@mui/material';
import ConstructionIcon from '@mui/icons-material/Construction';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { useDispatch } from 'react-redux';
import { updatePlatformSettings } from '../../slices/superAdminSlice';
import { useToast } from '../../context/ToastContext';
import { FEATURE_GROUPS, MODEL_FEATURE_DEFAULTS } from './featureFlags';
import { SA, cardSx } from './theme';

const LEVELS = [
  { value: 'info', label: 'معلومة' },
  { value: 'success', label: 'نجاح' },
  { value: 'warning', label: 'تنبيه' },
  { value: 'error', label: 'هام' },
];

const Card = ({ icon, title, subtitle, children, toggle }) => (
  <Box sx={{ ...cardSx, overflow: 'hidden' }}>
    <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: children ? `1px solid ${SA.border}` : 0 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: SA.primarySoft, color: SA.primary, display: 'grid', placeItems: 'center' }}>{icon}</Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography sx={{ fontWeight: 800, color: SA.text, fontFamily: SA.font }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: SA.muted, fontFamily: SA.font }}>{subtitle}</Typography>
      </Box>
      {toggle}
    </Box>
    {children && <Box sx={{ p: 2.5 }}>{children}</Box>}
  </Box>
);

const toForm = (s) => ({
  ...s,
  default_max_users: s.default_max_users ?? '',
  default_trial_days: s.default_trial_days ?? '',
  default_features: { ...MODEL_FEATURE_DEFAULTS, ...(s.default_features || {}) },
});

const SuperAdminSettingsTab = ({ settings }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm(toForm(settings));
  }, [settings]);

  if (!form) return <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setFeature = (key, value) => setForm((f) => ({ ...f, default_features: { ...f.default_features, [key]: value } }));
  const dirty = JSON.stringify(toForm(settings)) !== JSON.stringify(form);

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(updatePlatformSettings({
        maintenance_mode: form.maintenance_mode,
        maintenance_message: form.maintenance_message,
        announcement_enabled: form.announcement_enabled,
        announcement_text: form.announcement_text,
        announcement_level: form.announcement_level,
        default_max_students: Number(form.default_max_students) || 0,
        default_max_users: form.default_max_users === '' ? null : Number(form.default_max_users),
        default_trial_days: form.default_trial_days === '' ? null : Number(form.default_trial_days),
        default_features: form.default_features,
        expiry_warning_days: Number(form.expiry_warning_days) || 0,
      })).unwrap();
      showToast('تم حفظ إعدادات المنصة', 'success');
    } catch {
      // error toast handled by the page
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'grid', gap: 2, maxWidth: 900, pb: 10 }}>
      <Card
        icon={<ConstructionIcon />}
        title="وضع الصيانة"
        subtitle="يمنع جميع مستخدمي المدارس من تسجيل الدخول ويعرض لهم الرسالة التالية"
        toggle={<Switch color="warning" checked={!!form.maintenance_mode} onChange={(e) => set({ maintenance_mode: e.target.checked })} />}
      >
        <TextField
          fullWidth multiline minRows={2} label="رسالة الصيانة"
          value={form.maintenance_message || ''} onChange={(e) => set({ maintenance_message: e.target.value })}
        />
      </Card>

      <Card
        icon={<CampaignOutlinedIcon />}
        title="إعلان لجميع المدارس"
        subtitle="شريط يظهر أعلى الصفحة لكل مستخدمي المدارس"
        toggle={<Switch checked={!!form.announcement_enabled} onChange={(e) => set({ announcement_enabled: e.target.checked })} />}
      >
        <ToggleButtonGroup
          exclusive size="small" value={form.announcement_level}
          onChange={(e, v) => v && set({ announcement_level: v })}
          sx={{ mb: 2, direction: 'ltr' }}
        >
          {LEVELS.map((l) => <ToggleButton key={l.value} value={l.value} sx={{ fontFamily: SA.font, px: 2 }}>{l.label}</ToggleButton>)}
        </ToggleButtonGroup>
        <TextField
          fullWidth multiline minRows={2} label="نص الإعلان"
          placeholder="مثال: سيتم تحديث النظام يوم الجمعة الساعة 10 مساءً"
          value={form.announcement_text || ''} onChange={(e) => set({ announcement_text: e.target.value })}
        />
        {form.announcement_text && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 12, color: SA.muted, mb: 0.75, fontFamily: SA.font }}>معاينة</Typography>
            <Alert severity={form.announcement_level} sx={{ fontFamily: SA.font, direction: 'rtl', opacity: form.announcement_enabled ? 1 : 0.5 }}>
              {form.announcement_text}
            </Alert>
          </Box>
        )}
      </Card>

      <Card icon={<NotificationsActiveOutlinedIcon />} title="تنبيه انتهاء الاشتراك" subtitle="متى نبدأ بتنبيهك وتنبيه مدير المدرسة بقرب انتهاء الاشتراك">
        <TextField
          type="number" label="عدد الأيام قبل الانتهاء" sx={{ width: 240 }}
          value={form.expiry_warning_days ?? ''} onChange={(e) => set({ expiry_warning_days: e.target.value })}
          inputProps={{ min: 0 }}
        />
      </Card>

      <Card icon={<AddBusinessOutlinedIcon />} title="الإعدادات الافتراضية للمدارس الجديدة" subtitle="تُعبّأ تلقائياً عند إضافة مدرسة، ويمكن تعديلها لكل مدرسة">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
          <TextField type="number" label="الحد الأقصى للطلاب" value={form.default_max_students ?? ''} onChange={(e) => set({ default_max_students: e.target.value })} inputProps={{ min: 0 }} />
          <TextField type="number" label="الحد الأقصى للمستخدمين" placeholder="غير محدود" helperText="فارغ = غير محدود" value={form.default_max_users} onChange={(e) => set({ default_max_users: e.target.value })} inputProps={{ min: 0 }} />
          <TextField type="number" label="مدة الاشتراك (أيام)" placeholder="مفتوح" helperText="فارغ = بدون انتهاء" value={form.default_trial_days} onChange={(e) => set({ default_trial_days: e.target.value })} inputProps={{ min: 0 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: SA.text, mb: 1.5, fontFamily: SA.font }}>الميزات المفعّلة افتراضياً</Typography>
        {FEATURE_GROUPS.map((group) => (
          <Box key={group.key} sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12.5, color: SA.muted, mb: 1, fontFamily: SA.font }}>{group.label}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {group.flags.map((flag) => {
                const on = !!form.default_features[flag.key];
                return (
                  <Box
                    key={flag.key}
                    onClick={() => setFeature(flag.key, !on)}
                    sx={{
                      px: 1.5, py: 0.6, borderRadius: 99, cursor: 'pointer', fontSize: 13, fontFamily: SA.font, userSelect: 'none',
                      border: `1px solid ${on ? SA.primary : SA.border}`,
                      bgcolor: on ? SA.primary : '#fff',
                      color: on ? '#fff' : SA.muted,
                    }}
                  >
                    {flag.label}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Card>

      {/* Sticky save bar, only when something changed */}
      {dirty && (
        <Box sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 10, bgcolor: SA.ink, color: '#fff', borderRadius: 99, pl: 1, pr: 3, py: 1, display: 'flex', alignItems: 'center', gap: 2, boxShadow: '0 10px 30px rgba(20,18,43,0.25)' }}>
          <Typography sx={{ fontSize: 14, fontFamily: SA.font }}>لديك تغييرات غير محفوظة</Typography>
          <Button size="small" sx={{ color: 'rgba(255,255,255,0.7)' }} onClick={() => setForm(toForm(settings))}>تراجع</Button>
          <Button size="small" variant="contained" disableElevation disabled={saving} onClick={handleSave} sx={{ borderRadius: 99, bgcolor: SA.primary }}>حفظ</Button>
        </Box>
      )}
    </Box>
  );
};

export default SuperAdminSettingsTab;
